import dayjs from "dayjs";

const PROFIT_AGENCY_NAME_BY_JURISDICTION = {
  DC: "DOEE",
  MD: "OHEP",
  VA: "VA DSS",
};

// MD/VA/DC profit agencies each get two independent pledge types; all other
// (non-profit) agencies only ever have one generic pledge field.
const DUAL_LOCK_TYPES_BY_JURISDICTION = {
  MD: ["MEAP", "GARA"],
  VA: ["Fuel", "Crisis"],
  DC: ["Emergency", "Regular"],
};

const NON_PROFIT_LOCK_TYPE = "Non-Profit";

const findPledgeByType = (pledgeAmountDetail, dunningLockType) =>
  pledgeAmountDetail?.find(
    (d) => d.DunningLockType?.toLowerCase() === dunningLockType.toLowerCase()
  );

export const isProfitAgency = (agencyName, jurisdiction) =>
  !!agencyName &&
  agencyName === PROFIT_AGENCY_NAME_BY_JURISDICTION[jurisdiction];

const formatDate = (date) => (date ? dayjs(date).format("MM/DD/YYYY") : "");

const buildPledgeAmountRow = (detail, Jurisdiction, key) => (
  <div key={key}>
    - {formatDate(detail.CreatedOn)}
    {!(
      Jurisdiction === "DC" &&
      detail.DunningLockType?.toLowerCase() === "non-profit"
    ) && ` for ${detail.DunningLockType}`}
  </div>
);

const buildPledgeAmountTooltip = (
  pledgeAmountDetail,
  Jurisdiction,
  otherPledgesByAgency = []
) => {
  if (!pledgeAmountDetail?.length && !otherPledgesByAgency?.length) return "";
  return (
    <>
      Pledge Amount request placed on:
      {pledgeAmountDetail.map((detail, index) =>
        buildPledgeAmountRow(
          detail,
          Jurisdiction,
          `${detail.DunningLockType}-${index}`
        )
      )}
      {otherPledgesByAgency.map((detail, index) =>
        buildPledgeAmountRow(
          detail,
          Jurisdiction,
          `other-${detail.DunningLockType}-${index}`
        )
      )}
    </>
  );
};

const buildEmergencyHoldTooltip = (emergencyHoldDetail) => {
  if (!emergencyHoldDetail?.CreatedOn || !emergencyHoldDetail?.HoldEndDate)
    return "";
  return (
    <>
      Emergency hold request placed on {formatDate(emergencyHoldDetail.CreatedOn)},
      active through {formatDate(emergencyHoldDetail.HoldEndDate)}.
    </>
  );
};

export const getPledgeEmergencyHoldState = ({
  Jurisdiction,
  EmergencyHoldDetail,
  PledgeAmountDetail,
  OtherPledgesByAgency,
  agencies,
}) => {
  const hasEmergencyHoldDetail =
    !!EmergencyHoldDetail?.CreatedOn && !!EmergencyHoldDetail?.HoldEndDate;
  const hasPledgeDetail = !!PledgeAmountDetail?.length;
  const isMultipleAgency = (agencies?.length || 0) > 1;

  const emergencyHoldDisabled = hasEmergencyHoldDetail || hasPledgeDetail;
  const emergencyHoldTooltip = hasEmergencyHoldDetail
    ? buildEmergencyHoldTooltip(EmergencyHoldDetail)
    : hasPledgeDetail
    ? buildPledgeAmountTooltip(PledgeAmountDetail, Jurisdiction)
    : "";

  let pledgeAmountDisabled;
  if (isMultipleAgency) {
    pledgeAmountDisabled = false;
  } else {
    const singleAgencyName = agencies?.[0]?.AgencyName ?? agencies?.[0]?.label;
    const dualLockTypes = DUAL_LOCK_TYPES_BY_JURISDICTION[Jurisdiction];
    const isSingleProfitDualAgency =
      dualLockTypes && isProfitAgency(singleAgencyName, Jurisdiction);

    if (isSingleProfitDualAgency) {
      const pledgedLockTypes = new Set(
        PledgeAmountDetail?.map((d) => d.DunningLockType)
      );
      pledgeAmountDisabled = dualLockTypes.every((type) =>
        pledgedLockTypes.has(type)
      );
    } else {
      pledgeAmountDisabled = hasPledgeDetail;
    }
  }

  const pledgeAmountTooltip = buildPledgeAmountTooltip(
    PledgeAmountDetail,
    Jurisdiction,
    OtherPledgesByAgency
  );

  return {
    emergencyHoldDisabled,
    emergencyHoldTooltip,
    pledgeAmountDisabled,
    pledgeAmountTooltip,
    isMultipleAgency,
  };
};

// Resolves which amount field(s) the Pledge Amount modal should render for
// the currently selected (or sole) agency: a single "Non-Profit" field, or
// two fields (one per dual lock type) when the agency is OHEP/VA DSS, each
// already reflecting any existing pledge from PledgeAmountDetail. Returns
// null when no agency is resolved yet (nothing to render).
export const getPledgeAmountFormFields = ({
  agencyId,
  agencies,
  Jurisdiction,
  PledgeAmountDetail,
}) => {
  const isMultipleAgency = (agencies?.length || 0) > 1;
  const agency = isMultipleAgency
    ? agencies?.find((a) => a.AgencyId === agencyId || a.value === agencyId)
    : agencies?.[0];

  if (!agency) return null;

  const agencyName = agency.AgencyName ?? agency.label;
  const dualLockTypes = DUAL_LOCK_TYPES_BY_JURISDICTION[Jurisdiction];

  if (dualLockTypes && isProfitAgency(agencyName, Jurisdiction)) {
    const fields = dualLockTypes.map((lockType) => {
      const existing = findPledgeByType(PledgeAmountDetail, lockType);
      return {
        name: `PledgeAmount_${lockType}`,
        dunningLockType: lockType,
        label: `${lockType} Amount`,
        disabled: !!existing,
        existingAmount: existing?.Amount ?? null,
      };
    });
    return { isDual: true, fields };
  }

  const existingNonProfit = findPledgeByType(
    PledgeAmountDetail,
    NON_PROFIT_LOCK_TYPE
  );
  return {
    isDual: false,
    fields: [
      {
        name: "PledgeAmount",
        dunningLockType: NON_PROFIT_LOCK_TYPE,
        label: "Amount",
        disabled: !!existingNonProfit,
        existingAmount: existingNonProfit?.Amount ?? null,
      },
    ],
  };
};
